import {Inject, Service} from "vue-typedi";
import {fabricationApiClient} from "@/configuration/api-client";
import {OrdersList} from "@/model/OrdersList";
import {Order} from "@/model/Order"
import {OrderState} from "@/model/OrderState";
import {UpdateOrderRequest} from "@/model/UpdateOrderRequest";
import { StoreService } from "./StoreService";
import { OrderProduct } from "@/model/OrderProduct";
import { ProcessedQuantityRequest } from "@/model/order/ProcessedQuantityRequest";
import { SerializationHelper } from "@/helpers/SerializationHelper";
import {UpdateOrderRequirements} from "@/model/UpdateOrderRequirements";

@Service()
export class OrderService {

    @Inject()
    private storeService!: StoreService;

    private static readonly ORDERS_ENDPOINT: string = '/orders';
    private static readonly STORE_ID: string = "storeId";

    public getOrders(criteria: GetOrdersSearchCriteria): Promise<OrdersList> {
        const params = new URLSearchParams();
        params.append('store', criteria.store);
        if (criteria.zones) {
            criteria.zones.forEach(zone => params.append('zones', zone));
        }
        if (criteria.slots) {
            criteria.slots.forEach(slot => params.append('slots', slot + ''));
        }
        if (criteria.orderStatuses) {
            criteria.orderStatuses.forEach(status => params.append('orderStatuses', status));
        }

        return fabricationApiClient.get(OrderService.ORDERS_ENDPOINT, { params : params })
            .then((response: any) => {
                const data: OrdersList = response && response.data ? response.data : {};
                return Promise.resolve(SerializationHelper.toInstanceFromJsonObj(new OrdersList(), data));
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public getArchivedOrders(startDate: string, endDate: string, status: string, driveIds: string[]): Promise<Order[]>  {
        const params = new URLSearchParams();
        const store: string = sessionStorage.getItem(OrderService.STORE_ID);
        if (store) {
            params.append('store', store);
        }
        if (startDate) {
            params.append('startDate', startDate);
        }
        if (endDate) {
            params.append('endDate', endDate);
        }
        if (status) {
            params.append('status', status);
        }
        if (this.storeService.selectedZones) {
            params.append('zones', this.storeService.selectedZones);
        }
        if (driveIds) {
            driveIds.forEach(driveId => params.append('driveIds', driveId));
        }

        return fabricationApiClient.get(OrderService.ORDERS_ENDPOINT + "/archived", { params: params })
            .then((response: any) => {
                const data: Order[] = response && response.data ? response.data : [];
                return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Order(), data));
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public getArchivedOrdersAsCsv(startDate: string, endDate: string, status: string, searchTerms: string, driveIds: string[]): Promise<Blob>  {
        const params = new URLSearchParams();
        const store: string = sessionStorage.getItem(OrderService.STORE_ID);
        if (store) {
            params.append('store', store);
        }
        if (startDate) {
            params.append('startDate', startDate);
        }
        if (endDate) {
            params.append('endDate', endDate);
        }
        if (status) {
            params.append('status', status);
        }
        const zones = this.storeService.selectedAndEnabledZones;
        if (zones != null) {
            zones.forEach(zone => params.append('zones', zone));
        }
        if (searchTerms) {
            params.append('searchTerms', searchTerms);
        }
        if (driveIds) {
            driveIds.forEach(driveId => params.append('driveIds', driveId));
        }

        return fabricationApiClient.get(OrderService.ORDERS_ENDPOINT + "/archived/csv", { params: params, responseType: 'blob'})
            .then((response: any) => {
                return Promise.resolve(new Blob([response.data], {type: 'text/csv'}));
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public updateOrder(orderNumber: string, updateOrderRequest: UpdateOrderRequest): Promise<Order[]> {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        updateOrderRequest.storeId = sessionStorage.getItem(OrderService.STORE_ID);

        return fabricationApiClient.patch(OrderService.ORDERS_ENDPOINT + "/" + orderNumber, updateOrderRequest, config)
            .then((response: any) => {
                const data: Order[] = response && response.data ? response.data : [];
                return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Order(), data));
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public async changeOrderState(orderNumber: string, zone: string, newState: OrderState, products: OrderProduct[]): Promise<Order[]> {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = {
            targetStatus: newState,
            storeId: sessionStorage.getItem(OrderService.STORE_ID),
            zone: zone,
            updateProcessedQuantityRequests: this.populateProcessedQuantityRequests(newState, products)
        }

        try {
            const response = await fabricationApiClient.put(OrderService.ORDERS_ENDPOINT + "/" + orderNumber, body, config);
            const data: Order[] = response && response.data ? response.data : {};
            return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Order(), data));
        } catch (error) {
            return Promise.reject(error);
        }
    }

    public async updateOrderProcessedQuantities(orderNumber: string, zone: string, products: OrderProduct[]): Promise<Order[]> {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = {
            targetStatus: OrderState.PROCESSED,
            storeId: sessionStorage.getItem(OrderService.STORE_ID),
            zone: zone,
            updateProcessedQuantityRequests: this.populateProcessedQuantityRequests(OrderState.PROCESSED, products)
        };

        try {
            const response = await fabricationApiClient.put(OrderService.ORDERS_ENDPOINT + "/" + orderNumber, body, config);
            const data: Order[] = response && response.data ? response.data : [];
            return SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Order(), data);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    populateProcessedQuantityRequests(newState: OrderState, products: OrderProduct[]): ProcessedQuantityRequest[] {
        if(newState != OrderState.PROCESSED || !products || products.length == 0){
            return [];
        }
        return products.map(product => {
            return new ProcessedQuantityRequest({ cug: product.cug, quantity: product.processedQuantity, totalProcessedQuantity: product.variableMeasurement?.totalProcessed, totalPrice: product.totalPrice, isSubstituted: product.isSubstituted });
        });
    }

    public assignOrderPicker(orderNumber: string, storeId: string, zone: string): Promise<Order[]> {
        return fabricationApiClient.patch(`${OrderService.ORDERS_ENDPOINT}/${orderNumber}/order-picker`, null, {
            params: { store: storeId, zone }
        }).then((response: any) => {
            const data: Order[] = response && response.data ? response.data : [];
            return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Order(), data));
        });
    }

    public patchUpdateOrderRequirements(orderNumber: string, zone: string, updateOrderRequirements: UpdateOrderRequirements): Promise<void> {
        const storeId = sessionStorage.getItem(OrderService.STORE_ID);
        return fabricationApiClient.patch(`${OrderService.ORDERS_ENDPOINT}/${orderNumber}/update-order-requirements`, updateOrderRequirements, {params: { store: storeId, zone }})
          .then(() => {})
          .catch((error) => {
              return Promise.reject(error);
          });
    }
}

export class GetOrdersSearchCriteria {
    store: string;
    zones?: string[];
    slots?: Date[];
    orderStatuses?: string[];
}